import axios from "axios";
import { Auth0Provider } from "@auth0/auth0-react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { frFR } from "@mui/material/locale";
import * as Sentry from "@sentry/react";
import { CaptureConsole } from "@sentry/integrations";
import { PropsWithChildren, memo } from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { QueryClientProvider } from "react-query";
import { ReactQueryDevtools } from "react-query/devtools";
import SnackbarProvider from "react-simple-snackbar";
import WebFont from "webfontloader";
import { BreadcrumbProvider } from "$components";
import { store } from "$store";
import constants from "$utils/constants";
import history from "$utils/history";
import { queryClient } from "$utils/queryClient";
import { SocketProvider } from "$utils/socket";
import { theme } from "$utils/theme/bataillog";
import "./index.css";
import Route from "./Route";
import MaintenancePage from "./maintenance/MaintenancePage";
import reportWebVitals from "./reportWebVitals";
import { GlobalSnackbarListener } from "$components/GlobalSnackbarListener";

const initializeSentry = () => {
  Sentry.init({
    dsn:
      process.env.NODE_ENV === "production"
        ? process.env.SENTRY_DSN ??
          "https://d26f6a9e61e04ce4be503886396a0740@o4504560547069952.ingest.sentry.io/4504561953931264"
        : null,
    integrations: [
      new Sentry.BrowserTracing(),
      new CaptureConsole({
        levels: ["error"],
      }),
    ],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    defaultIntegrations: [new Sentry.Replay()],
  });
};

const initializeAxios401Interceptor = () => {
  axios.interceptors.response.use(
    (response: any) => response,
    (error: any) => {
      if (error?.response?.status === 401) {
        // eslint-disable-next-line no-console
        console.log(error?.response);

        const requestUrl: string = (error?.config?.url ?? "").toString();
        const isInseeRequest =
          requestUrl.includes("api.insee.fr") ||
          requestUrl.startsWith("/insee");
        const isFirstPartyApiRequest =
          requestUrl.includes(constants.API.ENDPOINT) ||
          requestUrl.startsWith(constants.API.ENDPOINT);

        if (isInseeRequest) {
          if (typeof window !== "undefined") {
            window.dispatchEvent(
              new CustomEvent("app:snackbar:error", {
                detail: {
                  message:
                    "Service INSEE indisponible ou non autorisé (401). Veuillez réessayer plus tard.",
                },
              })
            );
          }
          return Promise.reject(error);
        }

        if (!isFirstPartyApiRequest) {
          return Promise.reject(error);
        }

        if (
          typeof window !== "undefined" &&
          !window.location.pathname.includes("connexion") &&
          !window.location.pathname.includes("mot-de-passe")
        ) {
          window.localStorage.removeItem("authState");
          window.localStorage.removeItem("access_token");
          window.localStorage.removeItem("refresh_token");
          window.location.href = "/connexion";
        }
        return Promise.reject(error);
      }
      return Promise.reject(error);
    }
  );
};

const initializeHyperDX = async () => {
  const apiKey = import.meta.env.VITE_HYPERDX_API_KEY;

  if (!apiKey || typeof window === "undefined") {
    return;
  }

  try {
    const { default: HyperDX } = await import("@hyperdx/browser");

    HyperDX.init({
      apiKey,
      service:
        (import.meta.env.VITE_HYPERDX_SERVICE_NAME as string) ||
        "batail-log-frontend",
      tracePropagationTargets: [
        /localhost/i,
        /batail/i,
        /noria/i,
        /cleverapps\.io/i,
      ],
      consoleCapture: true,
      advancedNetworkCapture: true,
      url: (import.meta.env.VITE_HYPERDX_URL as string) || undefined,
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn("HyperDX initialization skipped", error);
  }
};

const isMaintenanceModeEnabled = () => {
  const raw = (process.env.MAINTENANCE_MODE ?? "").toString().trim();
  return ["1", "true", "yes", "on"].includes(raw.toLowerCase());
};

const muiTheme = createTheme(
  {
    palette: {
      primary: {
        main: "#3f51b5",
        dark: "#303f9f",
        light: "#7986cb",
      },
    },
    components: {
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            fontSize: "0.8rem",
          },
          popper: {
            zIndex: 9999,
          },
        },
      },
      MuiInputBase: {
        defaultProps: {
          disableInjectingGlobalStyles: true,
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            backgroundColor: theme.color.blueLight,
            height: 3,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontSize: 16,
          },
        },
      },
    },
    typography: {
      caption: undefined,
    },
  },
  frFR
);

WebFont.load({
  google: {
    families: [
      "Work Sans:700,600,500,400,300,200,100",
      "Roboto:700,600,500,400,300,200,100",
    ],
  },
});

const container = document.getElementById("root");

if (!container) {
  throw new Error('Root container with id "root" was not found');
}

const root = createRoot(container);

const MemoBreadcrumbProvider = memo(({ children }: PropsWithChildren) => {
  return <BreadcrumbProvider>{children}</BreadcrumbProvider>;
});

initializeSentry();
initializeAxios401Interceptor();
initializeHyperDX().catch((error) => {
  // eslint-disable-next-line no-console
  console.warn("HyperDX initialization failed", error);
});

if (isMaintenanceModeEnabled()) {
  root.render(<MaintenancePage />);
} else {
  const onRedirectCallback = (appState: any) => {
    history.push(
      appState && appState.returnTo
        ? appState.returnTo
        : window.location.pathname
    );
  };

  root.render(
    <ThemeProvider theme={muiTheme}>
      <ThemeProvider theme={theme}>
        <QueryClientProvider client={queryClient}>
          <ReactQueryDevtools initialIsOpen={false} />
          <Provider store={store}>
            <SnackbarProvider>
              <GlobalSnackbarListener />
              <Auth0Provider
                domain={constants.AUTH0.DOMAIN}
                clientId={constants.AUTH0.CLIENTID}
                authorizationParams={{
                  audience: constants.AUTH0.AUDIENCE,
                  redirect_uri: window.location.origin,
                }}
                onRedirectCallback={onRedirectCallback}
              >
                <SocketProvider>
                  <MemoBreadcrumbProvider>
                    <Route />
                  </MemoBreadcrumbProvider>
                </SocketProvider>
              </Auth0Provider>
            </SnackbarProvider>
          </Provider>
        </QueryClientProvider>
      </ThemeProvider>
    </ThemeProvider>
  );
}

reportWebVitals();
